import { TransactionReceipt } from 'viem'

import {
    BACKOFF_MULTIPLIER,
    INITIAL_POLLING_INTERVAL,
    MAXIMUM_RETRY_COUNT,
    MAX_POLLING_INTERVAL,
} from '@src/constants/exchange'
import { ResultCode } from '@src/enums'
import { getViemClient } from '@src/hooks/evm/useViemClient'
import { exchangeLogger } from '@src/utils/logger'
import { useHistoryStore } from '@src/zustand_stores/history.store'

/**
 * Helper function to get a transaction receipt using viem client
 * @param hash Transaction hash
 * @param chainId The EVM chain ID where the transaction was submitted
 * @returns Transaction receipt or undefined if not found
 */
async function viemGetTransactionReceipt(
    hash: `0x${string}`,
    chainId: number
): Promise<TransactionReceipt | undefined> {
    try {
        // Get the viem client for the specified chain
        const client = getViemClient(chainId)
        if (!client) {
            throw new Error(`No viem client available for chainId: ${chainId}`)
        }

        exchangeLogger.info(`Getting transaction receipt for chain ${chainId}`, { hash, chainId })

        try {
            // Get the transaction receipt
            const receipt = await client.getTransactionReceipt({ hash })
            return receipt
        } catch (e) {
            exchangeLogger.error(`Error getting transaction receipt on chain ${chainId}:`, {
                error: e,
                hash,
                chainId,
            })
            return undefined
        }
    } catch (error) {
        exchangeLogger.error(`Error getting transaction receipt: ${(error as Error).message}`, { hash, chainId })
        return undefined
    }
}

export const useWaitForTransactionEvm = () => {
    const updateHistory = useHistoryStore(state => state.updateHistory)

    const waitForTransactionReceipt = async (hash: `0x${string}`, chainId?: number) => {
        return new Promise((resolve, reject) => {
            // Pass the retryCount as a parameter to track it properly in the recursive function
            const attemptGetReceipt = async (currentRetryCount: number) => {
                if (currentRetryCount >= MAXIMUM_RETRY_COUNT) {
                    updateHistory(null, {
                        result: {
                            sourceTxHash: ``,
                            message: 'We are so sorry that this order failed. Please try again.',
                            status: ResultCode.failure,
                        },
                    })
                    reject({ message: 'Exceeded maximum retry count' })
                    return
                }

                try {
                    // Extract chainId from the transaction hash if needed
                    // For now we'll determine the chainId from the transaction later
                    // Let viem handle this for us

                    // Log transaction details to help troubleshoot issues with specific networks
                    exchangeLogger.info('Waiting for transaction receipt:', {
                        transactionHash: hash,
                        retryCount: currentRetryCount,
                    })

                    // If chainId isn't provided, default to current chain ID
                    // This should rarely happen as the calling code should provide chainId
                    const effectiveChainId = chainId || 1 // Default to Ethereum mainnet as a fallback

                    exchangeLogger.info('Using chain ID for transaction receipt:', {
                        chainId: effectiveChainId,
                        providedChainId: chainId !== undefined,
                    })

                    // Get the transaction receipt using the viem client for the specific chain
                    const receipt = await viemGetTransactionReceipt(hash, effectiveChainId)

                    exchangeLogger.info('Transaction receipt:', { receipt })

                    if (receipt) {
                        resolve(receipt)
                        return
                    }

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, currentRetryCount),
                        MAX_POLLING_INTERVAL
                    )
                    const nextRetryCount = currentRetryCount + 1
                    setTimeout(() => attemptGetReceipt(nextRetryCount), nextInterval)
                } catch (error) {
                    // Enhance error logging with more context
                    exchangeLogger.error(`getTransactionReceipt error: \n${(error as Error).message}`, {
                        error,
                        hash,
                        retryCount: currentRetryCount,
                    })

                    const nextInterval = Math.min(
                        INITIAL_POLLING_INTERVAL * Math.pow(BACKOFF_MULTIPLIER, currentRetryCount),
                        MAX_POLLING_INTERVAL
                    )
                    const nextRetryCount = currentRetryCount + 1
                    setTimeout(() => attemptGetReceipt(nextRetryCount), nextInterval)
                }
            }

            attemptGetReceipt(0)
        })
    }

    // No need for useEffect to reset retry count anymore, as we're using function parameters
    return { waitForTransactionReceipt }
}
